import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";

const API_URL = process.env.REACT_APP_API_URL;

// ==============================================
// TYPES - PAIEMENT STRIPE
// ==============================================

export interface PaymentIntentResponse {
  success: boolean;
  clientSecret: string;
}

export interface ConfirmPremiumPurchaseParams {
  paymentIntentId: string;
  professionId: number;
  userId: number;
}

export interface PremiumPurchaseConfirmResult {
  success: boolean;
  position: number;
  profession: string;
  message: string;
}

// ✅ NOUVEAU TYPE - Pour l'export
export interface ConfirmExportPurchaseParams {
  paymentIntentId: string;
  amount: number;
  userId: number;
  professionName?: string;  // Ajouté pour les emails
  fileName?: string;        // Ajouté pour les emails
}

let stripePromise: Promise<any> | null = null;

export const getStripe = () => {
  if (!stripePromise) {
    // ✅ Correction: Utiliser directement process.env
    const stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
    
    if (!stripeKey) {
      console.error("❌ REACT_APP_STRIPE_PUBLIC_KEY n'est pas définie dans .env");
    } else {
      console.log("✅ Stripe key chargée");
    }
    
    stripePromise = loadStripe(stripeKey || '');
  }
  return stripePromise;
};

// ==============================================
// PAIEMENT PREMIUM
// ==============================================

/**
 * Créer un PaymentIntent pour l'achat Premium
 */
export const createPremiumPaymentIntent = async (
  amount: number,
  professionId: number,
  userId: number | null
): Promise<PaymentIntentResponse> => {
  try {
    console.log(`💰 Création PaymentIntent Premium: ${amount}€ pour profession ${professionId}`);

    const response = await axios.post(`${API_URL}/profession/create-premium-intent`, {
      amount,
      userId,
      professionId
    });

    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la création du paiement");
    }
  } catch (error: any) {
    console.error("❌ Erreur createPremiumPaymentIntent:", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

/**
 * Vérifier le statut d'un paiement
 */
export const verifyPayment = async (paymentIntentId: string) => {
  try {
    const response = await axios.get(`${API_URL}/profession/verify-payment/${paymentIntentId}`);
    return response.data;
  } catch (error: any) {
    console.error("❌ Erreur verifyPayment:", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

/**
 * Confirmer l'achat premium APRÈS paiement réussi
 */
export const confirmPremiumPurchase = async (
  params: ConfirmPremiumPurchaseParams
): Promise<PremiumPurchaseConfirmResult> => {
  try {
    console.log(`🔍 Confirmation achat premium - PaymentIntent: ${params.paymentIntentId}`);

    const response = await axios.post(`${API_URL}/profession/confirm-premium-purchase`, {
      paymentIntentId: params.paymentIntentId,
      professionId: params.professionId,
      userId: params.userId
    });

    if (response.data.success) {
      return {
        success: true,
        position: response.data.data?.position || response.data.position,
        profession: response.data.data?.profession || response.data.profession,
        message: response.data.message
      };
    } else {
      throw new Error(response.data.message || "Erreur lors de la confirmation de l'achat");
    }
  } catch (error: any) {
    console.error("❌ Erreur confirmPremiumPurchase:", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

// ==============================================
// ✅ PAIEMENT EXPORT - DIRECT (sans crédits)
// ==============================================

/**
 * Créer un PaymentIntent pour l'achat d'EXPORT
 * POST /api/profession/create-export-intent
 */
export const createExportPaymentIntent = async (
  amount: number,
  userId: number | null
): Promise<PaymentIntentResponse> => {
  try {
    console.log(`💰 Création PaymentIntent Export: ${amount}€`);

    const response = await axios.post(`${API_URL}/profession/create-export-intent`, {
      amount,
      userId
    });

    console.log("✅ PaymentIntent export créé:", response.data);

    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || "Erreur lors de la création du paiement");
    }
  } catch (error: any) {
    console.error("❌ Erreur createExportPaymentIntent:", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

/**
 * Confirmer l'achat EXPORT APRÈS paiement réussi
 * POST /api/profession/confirm-export-purchase
 */
export const confirmExportPurchase = async (
  params: ConfirmExportPurchaseParams
): Promise<any> => {
  try {
    console.log(`🔍 Confirmation achat export - PaymentIntent: ${params.paymentIntentId}`);

    const response = await axios.post(`${API_URL}/profession/confirm-export-purchase`, {
      paymentIntentId: params.paymentIntentId,
      amount: params.amount,
      userId: params.userId,
      professionName: params.professionName,
      fileName: params.fileName
    });

    console.log("✅ Achat export confirmé:", response.data);
    return response.data;
  } catch (error: any) {
    console.error("❌ Erreur confirmExportPurchase:", error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

/**
 * Récupérer la clé publique Stripe
 */
export const getStripePublicKey = async (): Promise<string> => {
  try {
    const response = await axios.get(`${API_URL}/profession/stripe-public-key`);
    return response.data.publicKey;
  } catch (error) {
    console.warn("⚠️ Impossible de récupérer la clé publique, utilisation de la clé du .env");
    return process.env.REACT_APP_STRIPE_PUBLIC_KEY || '';
  }
};

/**
 * Formater le prix pour Stripe (centimes → euros)
 */
export const formatAmountForStripe = (amount: number): number => {
  return Math.round(amount * 100);
};

/**
 * Déformater le prix de Stripe (euros → centimes)
 */
export const formatAmountFromStripe = (amount: number): number => {
  return amount / 100;
};